/**
 * What a new row in this view starts with.
 *
 * A default belongs to the view, not to the column: a view filtered to one
 * stage can start every row it makes in that stage without the column meaning
 * that anywhere else. See `default-values.ts` for the two ways a default can be
 * said and why they are exclusive.
 *
 * The value is edited by the field's *own* editor -- the same component the
 * grid opens over a cell and the row modal mounts -- so a date default is
 * picked from a calendar and a select default from its options, and nothing
 * here knows which types those are.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { Field, View } from '@/api/types'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Label } from '@/shared/ui/label'
import { Switch } from '@/shared/ui/switch'
import { NativeSelect } from '@/shared/ui/native-select'
import { useFieldContext } from '../fields/ui/context'
import { editorFor } from '../fields/ui/edit'
import {
  defaultValueFunctions,
  defaultValuesBody,
  draftsFrom,
  type DefaultValueDraft,
  type DefaultValueFunction,
} from './default-values'
import { patchView } from './view-cache'

/** A stored value rather than a function, which is the mode with an editor. */
const STATIC = ''

export function DefaultValuesDialog({
  tableId,
  view,
  fields,
  onClose,
}: {
  tableId: number
  view: View
  /** Every field of the table: a default is about the row, not about the view. */
  fields: readonly Field[]
  onClose: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  // Derived here rather than passed in: the workspace is on every field, and a
  // collaborator default is the one thing in this dialog that needs it.
  const context = useFieldContext(fields[0]?.workspace_id)

  // Only the columns something can be typed into. `editorFor` already refuses a
  // read-only field, which covers a formula, a lookup and a column a data sync
  // owns -- none of which a default could be applied to.
  const editable = useMemo(
    () => fields.filter((field) => editorFor(field) !== null),
    [fields],
  )

  const [drafts, setDrafts] = useState<DefaultValueDraft[]>(() =>
    editable.map((field) =>
      draftsFrom(view.default_row_values ?? [], field, (value) =>
        editorFor(field)!.toDraft(field, value),
      ),
    ),
  )

  const save = useMutation({
    mutationFn: (body: unknown) =>
      unwrap(
        getApi().PATCH('/api/database/views/{view_id}/default-values/', {
          params: { path: { view_id: view.id } },
          body: body as never,
        }),
      ),
    // Written back from the answer rather than patched optimistically: the
    // server assigns an id to each entry and decides what it made of a value,
    // and a default nobody can see the effect of yet is not worth guessing at.
    onSuccess: (answer) => {
      queryClient.setQueryData<View[]>(qk.views(tableId), (views) =>
        views === undefined
          ? views
          : patchView(views, view.id, (held) => ({
              ...held,
              default_row_values: answer,
            })),
      )
      onClose()
    },
  })

  function update(fieldId: number, change: Partial<DefaultValueDraft>): void {
    setDrafts((current) =>
      current.map((draft) =>
        draft.fieldId === fieldId ? { ...draft, ...change } : draft,
      ),
    )
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="flex max-h-[80vh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle>{t('defaults.title')}</DialogTitle>
          <DialogDescription>
            {t('defaults.description', { name: view.name })}
          </DialogDescription>
        </DialogHeader>

        <ul
          aria-label={t('defaults.title')}
          className="min-h-0 flex-1 space-y-4 overflow-y-auto"
        >
          {editable.map((field) => {
            const draft = drafts.find((entry) => entry.fieldId === field.id)
            if (draft === undefined) return null
            const editor = editorFor(field)
            if (editor === null) return null
            const functions = defaultValueFunctions(field)

            return (
              <li key={field.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor={`default-${field.id}`} className="text-sm">
                    {field.name}
                  </Label>
                  <Switch
                    id={`default-${field.id}`}
                    checked={draft.enabled}
                    onCheckedChange={(on) => update(field.id, { enabled: on })}
                  />
                </div>

                {draft.enabled && (
                  <>
                    {/* Offered only where the type has one. Exactly one type
                        does, which is why this is a list and not a checkbox. */}
                    {functions.length > 0 && (
                      <NativeSelect
                        aria-label={t('defaults.mode', { field: field.name })}
                        value={draft.function ?? STATIC}
                        onChange={(event) =>
                          update(field.id, {
                            function:
                              event.target.value === STATIC
                                ? null
                                : (event.target.value as DefaultValueFunction),
                          })
                        }
                      >
                        <option value={STATIC}>{t('defaults.staticValue')}</option>
                        {functions.map((name) => (
                          <option key={name} value={name}>
                            {t(`defaults.functions.${name}`)}
                          </option>
                        ))}
                      </NativeSelect>
                    )}

                    {draft.function === null && (
                      <editor.Component
                        field={field}
                        value={draft.draft}
                        onValueChange={(value) => update(field.id, { draft: value })}
                        onKeyDown={() => {}}
                        // Not the column's name: the switch above already
                        // carries that, and two controls announcing "Stage" is
                        // ambiguous to anybody who cannot see which is which.
                        label={t('defaults.valueFor', { field: field.name })}
                        context={context}
                        // Every editor is mounted at once here, as in a form:
                        // twenty of them each taking the focus would leave it on
                        // whichever happened to mount last.
                        autoFocus={false}
                      />
                    )}
                  </>
                )}
              </li>
            )
          })}
        </ul>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            disabled={save.isPending}
            onClick={() => {
              save.mutate(
                defaultValuesBody(drafts, (draft) => {
                  const field = editable.find((one) => one.id === draft.fieldId)
                  if (field === undefined) return null
                  return editorFor(field)?.fromDraft(field, draft.draft, context) ?? null
                }),
              )
            }}
          >
            {t('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
